import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "./AuthContext";

const OrderContext = createContext(null);

// Line price + unit label for the purchase unit chosen on an offer (BOX £32.99 vs EACH £8.99); falls back to the product's single price.
export const priceFor = (product, purchaseUnit) => {
  const e = purchaseUnit ? (product.purchase_units || []).find((u) => u.key === purchaseUnit) : null;
  if (e) return { price: e.price ?? null, unit: e.label, purchase_unit: e.key };
  return { price: product.price, unit: product.unit, purchase_unit: purchaseUnit || null };
};

// Draft order state: items keyed by supplier_product_id, auto-saved to the server (debounced) + localStorage.
export function OrderProvider({ children }) {
  const { restaurantId, session } = useAuth();
  const key = `supplivo_draft_${restaurantId}`;
  const [items, setItems] = useState({});
  const [note, setNote] = useState("");
  const [orderId, setOrderId] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const timer = useRef(null);
  const dirty = useRef(false);

  useEffect(() => {
    if (!session || !restaurantId) return;
    setLoaded(false);
    const local = JSON.parse(localStorage.getItem(key) || "null");
    api.get("/ordering/orders/draft", { params: { restaurant_id: restaurantId } }).then(({ data }) => {
      if (data) {
        const map = {};
        data.items.forEach((i) => { map[i.supplier_product_id] = { quantity: i.quantity, manual: i.supplier_manual, description: i.description, supplier_name: i.supplier_name, price: i.snapshot?.price, purchase_unit: i.purchase_unit || null, unit: i.unit }; });
        setItems(map); setNote(data.note || ""); setOrderId(data.id);
      } else if (local) {
        setItems(local.items || {}); setNote(local.note || ""); setOrderId(null); dirty.current = true;
      } else { setItems({}); setNote(""); setOrderId(null); }
      setLoaded(true);
    }).catch(() => { if (local) { setItems(local.items || {}); setNote(local.note || ""); } setLoaded(true); });
  }, [restaurantId, session, key]);

  const persist = useCallback(async (nextItems, nextNote, id) => {
    const payload = { items: Object.entries(nextItems).map(([sid, v]) => ({ supplier_product_id: sid, quantity: v.quantity, supplier_manual: !!v.manual, purchase_unit: v.purchase_unit || null })), note: nextNote };
    try {
      if (id) await api.put(`/ordering/orders/${id}`, payload);
      else if (payload.items.length) { const { data } = await api.post("/ordering/orders", { restaurant_id: restaurantId, ...payload }); setOrderId(data.id); }
    } catch { /* keep local copy */ }
  }, [restaurantId]);

  useEffect(() => {
    if (!loaded || !dirty.current) return;
    localStorage.setItem(key, JSON.stringify({ items, note }));
    clearTimeout(timer.current);
    timer.current = setTimeout(() => persist(items, note, orderId), 700);
    return () => clearTimeout(timer.current);
  }, [items, note, orderId, loaded, key, persist]);

  const setQty = useCallback((product, qty, manual) => {
    dirty.current = true;
    setItems((prev) => {
      const next = { ...prev };
      if (qty <= 0) delete next[product.supplier_product_id];
      else {
        const pu = prev[product.supplier_product_id]?.purchase_unit || product.default_purchase_unit || null;
        next[product.supplier_product_id] = { quantity: qty, manual: manual ?? prev[product.supplier_product_id]?.manual ?? false, description: product.description, supplier_name: product.supplier_name, ...priceFor(product, pu) };
      }
      return next;
    });
  }, []);

  // BOX ↔ EACH on an existing line: one purchase unit per line, price follows the unit, quantity stays whatever the user typed
  const setUnit = useCallback((product, purchaseUnit, qty) => {
    dirty.current = true;
    setItems((prev) => {
      const cur = prev[product.supplier_product_id];
      const quantity = qty ?? cur?.quantity ?? 0;
      if (quantity <= 0) return prev;
      return { ...prev, [product.supplier_product_id]: { ...(cur || { manual: false, description: product.description, supplier_name: product.supplier_name }), quantity, ...priceFor(product, purchaseUnit) } };
    });
  }, []);

  // Mobile basket variant: keeps the line in the basket at quantity 0 so the user can swipe-delete
  // explicitly instead of losing the product the moment they tap "−" too many times.
  const setQtyKeepZero = useCallback((product, qty, manual) => {
    dirty.current = true;
    setItems((prev) => {
      const next = { ...prev };
      const q = Math.max(0, qty);
      next[product.supplier_product_id] = { quantity: q, manual: manual ?? prev[product.supplier_product_id]?.manual ?? false, description: product.description, supplier_name: product.supplier_name, ...priceFor(product, prev[product.supplier_product_id]?.purchase_unit || product.default_purchase_unit || null) };
      return next;
    });
  }, []);

  const removeItem = useCallback((sid) => {
    dirty.current = true;
    setItems((prev) => { const next = { ...prev }; delete next[sid]; return next; });
  }, []);

  const restoreItem = useCallback((sid, snapshot) => {
    if (!snapshot) return;
    dirty.current = true;
    setItems((prev) => ({ ...prev, [sid]: snapshot }));
  }, []);

  const swapSupplier = useCallback((fromId, toOption, qty) => {
    dirty.current = true;
    setItems((prev) => { const next = { ...prev }; delete next[fromId]; next[toOption.supplier_product_id] = { quantity: qty, manual: true, description: toOption.description, supplier_name: toOption.supplier_name, ...priceFor(toOption, toOption.default_purchase_unit || null) }; return next; });
  }, []);

  const clear = useCallback(() => { dirty.current = false; setItems({}); setNote(""); setOrderId(null); localStorage.removeItem(key); }, [key]);
  // Replace the basket with an order already saved on the server (e.g. after Merge Orders) — nothing to re-persist.
  const loadServerOrder = useCallback((data) => {
    dirty.current = false;
    clearTimeout(timer.current);
    const map = {};
    (data?.items || []).forEach((i) => { map[i.supplier_product_id] = { quantity: i.quantity, manual: i.supplier_manual, description: i.description, supplier_name: i.supplier_name, price: i.snapshot?.price, purchase_unit: i.purchase_unit || null, unit: i.unit }; });
    setItems(map); setNote(data?.note || ""); setOrderId(data?.id || null);
    localStorage.setItem(key, JSON.stringify({ items: map, note: data?.note || "" }));
  }, [key]);
  const updateNote = (n) => { dirty.current = true; setNote(n); };
  const flush = useCallback(async () => { clearTimeout(timer.current); await persist(items, note, orderId); }, [persist, items, note, orderId]);

  const value = useMemo(() => ({ items, note, orderId, loaded, setQty, setUnit, setQtyKeepZero, removeItem, restoreItem, swapSupplier, clear, loadServerOrder, updateNote, flush,
    count: Object.values(items).reduce((a, v) => a + v.quantity, 0) }), [items, note, orderId, loaded, setQty, setUnit, setQtyKeepZero, removeItem, restoreItem, swapSupplier, clear, loadServerOrder, flush]);
  return <OrderContext.Provider value={value}>{children}</OrderContext.Provider>;
}

export const useOrder = () => useContext(OrderContext);
